import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import './SimplisticServices.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { shouldInvertIcons } from '../utils/widgetUtils';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import HighlightText from '../WidgetEditComponents/HighlightText';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function SimplisticServices({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    console.log('🔍 SimplisticServices rendered:', { 
        id: data.id, 
        type: data.type, 
        isSelected, 
        isEditMode,
        hasOnSelectWidget: !!onSelectWidget,
        servicesCount: data.services?.length || 0,
        services: data.services?.map(s => ({
            name: s.name,
            useImage: s.useImage,
            serviceImage: s.serviceImage,
            hasIcon: !!(s.icon || s.customIcon)
        }))
    });
    
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // ✅ INLINE EDITING STATES - EXAKT WIE HEROHEADER
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    
    // Animation Trigger State (2025 Animation System)
    const [animationKey, setAnimationKey] = useState(0);
    
    // Ref für Outside-Click Detection
    const widgetRef = useRef(null);
    const containerRef = useRef(null);

    // Mobile Layout Detection (750px Breakpoint)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // 🚀 RESPONSIVE WIDGET STYLES mit generateLayoutStyles
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: data.maxWidth ? appendPx(isMobileLayout ? 
            (data.maxWidthMobile || data.maxWidth) : 
            data.maxWidth
        ) : 'none'
    });

    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Height-Property (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 500)
        }),
        // ✅ FALLBACK FIX: Nur wenn KEINE Responsive Padding Properties gesetzt sind
        ...((layoutStyles.paddingTop === '0px' && layoutStyles.paddingBottom === '0px') && 
           (data.paddingTop === undefined && data.paddingTopMobile === undefined && 
            data.paddingBottom === undefined && data.paddingBottomMobile === undefined) && {
            padding: appendPx(data.padding || 40)
        })
    };

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // Widget Click Handler
    const handleWidgetClick = (event) => {
        console.log('🔍 SimplisticServices clicked:', { 
            isEditMode, 
            hasOnSelectWidget: !!onSelectWidget, 
            widgetId: data.id,
            target: event.target.tagName
        });
        
        if (isEditMode && onSelectWidget && !isInlineEditing) {
            event.stopPropagation();
            onSelectWidget(data.id);
        }
    };

    // Smart Outside-Click Handler - nur für echte Außenklicks
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            // Smarte Erkennung: Ist es ein echter Außenklick?
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            const isIconPicker = event.target.closest('.iconPicker') || event.target.closest('.icon-picker-container');
            
            // Definiere was ein "echter Außenklick" ist
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     !isIconPicker &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            console.log('🔍 SimplisticServices smart outside click analysis:', {
                isInlineEditing,
                target: event.target.tagName,
                targetClass: event.target.className,
                isEditingInput,
                isActionButton,
                isDeleteButton,
                isWidgetEdit,
                isIconPicker,
                isPagePreview,
                isRealOutsideClick,
                inlineValue
            });
            
            if (isRealOutsideClick) {
                console.log('✅ Real outside click confirmed - saving and closing');
                // Speichern und beenden
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                    console.log('💾 Saved data:', updatedData);
                }
                // Edit-Modus beenden
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            console.log('🎯 Adding smart outside click listener for SimplisticServices field:', isInlineEditing);
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                console.log('🧹 Removing smart outside click listener');
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    // Inline Editing Functions
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!isEditMode || !onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        // Toggle functionality: If already editing this field, cancel editing
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = (fieldName) => {
        console.log('💾 SimplisticServices handleInlineSubmit called:', { fieldName, inlineValue, hasOnUpdate: !!onUpdate });
        
        if (!onUpdate) {
            console.error('❌ No onUpdate function provided');
            return;
        }
        
        if (!fieldName || fieldName !== isInlineEditing) {
            console.error('❌ Invalid fieldName or not currently editing');
            return;
        }
        
        // Update the widget data
        const updatedData = { ...data, [fieldName]: inlineValue };
        console.log('📝 Updating SimplisticServices data:', updatedData);
        onUpdate(updatedData);
        
        // Reset inline editing state
        setIsInlineEditing(null);
        setInlineValue('');
        console.log('✅ SimplisticServices inline editing ended');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        // Dynamische Detection: Ist es gerade ein Textarea?
        const isCurrentlyTextarea = e.target.tagName === 'TEXTAREA';
        
        if (e.key === 'Enter') {
            if (isCurrentlyTextarea) {
                // Textareas: Enter = neue Zeile, Ctrl+Enter = speichern
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    handleInlineSubmit(fieldName);
                }
                // Enter allein = neue Zeile (normales Verhalten)
            } else {
                // Input-Felder: Enter = speichern
                e.preventDefault();
                handleInlineSubmit(fieldName);
            }
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    // Render inline editable text
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            return currentValue ? (
                <Element style={{
                    ...style,
                    whiteSpace: style.whiteSpace || 'pre-wrap' // ← Behält Original-whiteSpace bei
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            // Verwende die gespeicherten Original-Layout-Eigenschaften
            const layoutData = originalLayout;
            
            // Smart Element Detection: Text-Elemente als Textarea
            const isTextarea = true; // SimplisticServices hat nur Text-Elemente
            
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: '-11px',
                    right: '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    {/* Grüner Haken - Speichern */}
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#059669';
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.boxShadow = '0 2px 6px rgba(16, 185, 129, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#10b981';
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 1px 3px rgba(16, 185, 129, 0.3)';
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    {/* Rotes X - Abbrechen */}
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#dc2626';
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.boxShadow = '0 2px 6px rgba(239, 68, 68, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#ef4444';
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = '0 1px 3px rgba(239, 68, 68, 0.3)';
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );
            
            // TEXTAREAS: Für alle Text-Elemente
            return (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            // ✅ Cursor-Position merken BEVOR wir die Höhe ändern
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                // ✅ Cursor-Position wiederherstellen
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            handleInlineKeyPress(e, fieldName);
                            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                setTimeout(() => {
                                    const target = e.target;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        placeholder={placeholder}
                        autoFocus
                        ref={(textarea) => {
                            if (textarea) {
                                setTimeout(() => {
                                    const originalHeight = parseInt(layoutData.height) || parseInt(style.fontSize) * 1.6;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = Math.max(textarea.scrollHeight, originalHeight) + 'px';
                                    // ✅ NUR beim ersten Laden ans Ende setzen, nicht bei jedem Re-Render
                                    if (textarea.selectionStart === 0 && textarea.selectionEnd === 0) {
                                        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                                    }
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '0',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            color: '#333333',
                            backgroundImage: 'none',
                            backgroundClip: 'unset',
                            webkitBackgroundClip: 'unset',
                            webkitTextFillColor: '#333333',
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            letterSpacing: style.letterSpacing,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: style.whiteSpace || 'pre-wrap' // ← Behält Original-whiteSpace bei
                        }}
                    />
                    {renderActionButtons()}
                </div>
            );
        }

        // Edit-Modus aber nicht gerade editiert: Normale Darstellung mit Click-Handler
        const handleElementClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px',
                marginTop: computedStyle.marginTop,
                marginBottom: computedStyle.marginBottom,
                marginLeft: computedStyle.marginLeft,
                marginRight: computedStyle.marginRight
            };
            
            setOriginalLayout(layoutData);
            handleInlineEdit(fieldName, currentValue);
        };

        // Normal display with hover effects
        return React.createElement(Element, {
            style: {
                ...style,
                cursor: isEditMode ? 'text' : 'default',
                transition: 'all 0.3s ease',
                whiteSpace: 'pre-wrap'  // ✅ KRITISCH: Whitespace für Anzeige beibehalten
            },
            onClick: isEditMode ? handleElementClick : undefined,
            onMouseEnter: isEditMode ? (e) => {
                // Text-Element Hover-Effekte
                e.target.style.background = 'linear-gradient(45deg, rgba(255, 255, 255, 0.02) 0%, rgba(192, 192, 192, 0.04) 25%, rgba(255, 255, 255, 0.06) 50%, rgba(192, 192, 192, 0.04) 75%, rgba(255, 255, 255, 0.02) 100%)';
                e.target.style.animation = 'silverGlow 3s ease-in-out infinite';
                e.target.style.backdropFilter = 'blur(8px)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            } : undefined,
            onMouseLeave: isEditMode ? (e) => {
                e.target.style.background = 'transparent';
                e.target.style.animation = 'none';
                e.target.style.backdropFilter = 'none';
                e.target.style.boxShadow = 'none';
            } : undefined
        }, currentValue || placeholder);
    };

    // Services aus data extrahieren - dynamisches Array
    const rawServices = data.services || [];
    
    // ✅ BACKWARDS COMPATIBILITY: Migriere alte mediaType zu useImage
    const allServices = rawServices.map(service => {
        // Wenn useImage nicht definiert ist, aber mediaType existiert
        if (service.useImage === undefined && service.mediaType !== undefined) {
            console.log('🔄 Migrating service from mediaType to useImage:', {
                serviceName: service.name,
                oldMediaType: service.mediaType,
                newUseImage: service.mediaType === 'image'
            });
            return {
                ...service,
                useImage: service.mediaType === 'image'
            };
        }
        return service;
    });
    
    // ✅ FILTER VISIBLE SERVICES - Nur sichtbare Services anzeigen
    const services = allServices.filter(service => service.visible !== false);
    
    console.log('🔍 SimplisticServices services data:', {
        allServicesArray: allServices,
        allServicesLength: allServices.length,
        visibleServicesArray: services,
        visibleServicesLength: services.length,
        firstService: services[0],
        hiddenServicesCount: allServices.length - services.length,
        servicesWithVisibility: allServices.map((s, i) => ({ 
            index: i, 
            name: s.name, 
            visible: s.visible,
            useImage: s.useImage,
            hasImage: !!s.serviceImage,
            hasIcon: !!(s.icon || s.customIcon)
        })),
        dataKeys: Object.keys(data)
    });

    // Layout Configuration (wie SpiritualTestimonial)
    const getLayoutConfig = () => {
        const layoutType = data.serviceLayout || 'classic-grid';
        
        const layouts = {
            'classic-grid': {
                servicesPerRow: 2,
                cardStyle: 'professional',
                iconPosition: 'top',
                hoverEffect: true
            },
            'minimal-cards': {
                servicesPerRow: 2,
                cardStyle: 'minimal',
                iconPosition: 'top',
                hoverEffect: true
            },
            'luxury-cards': {
                servicesPerRow: 2,
                cardStyle: 'premium',
                iconPosition: 'top',
                hoverEffect: true
            },
            'bordered-minimal-clean': {
                servicesPerRow: 4,
                cardStyle: 'clean-bordered',
                iconPosition: 'top',
                hoverEffect: true
            },
            'minimal-four': {
                servicesPerRow: 2, // ✅ 2 SPALTEN
                cardStyle: 'minimal-bordered',
                iconPosition: 'top',
                hoverEffect: true
            },
            'glass-panels': {
                servicesPerRow: 3,
                cardStyle: 'modern',
                iconPosition: 'top',
                hoverEffect: true
            },
            'masonry-flow': {
                servicesPerRow: 3,
                cardStyle: 'organic',
                iconPosition: 'top',
                hoverEffect: true
            },
            'icon-left': {
                servicesPerRow: 2,
                cardStyle: 'horizontal',
                iconPosition: 'left',
                hoverEffect: true
            },
            'three-column-clean': {
                servicesPerRow: 3, // ✅ 3 SPALTEN
                cardStyle: 'clean-professional',
                iconPosition: 'top',
                hoverEffect: true
            }
        };
        
        return layouts[layoutType] || layouts['classic-grid'];
    };

    const layoutConfig = getLayoutConfig();

    // Layout Helper Functions - Grid Layout
    const getLayoutStyle = () => {
        const servicesPerRow = data.servicesPerRow || layoutConfig.servicesPerRow || 2;
        const servicesPerRowMobile = data.servicesPerRowMobile || 1;
        
        return {
            display: 'grid',
            gridTemplateColumns: isMobileLayout 
                ? `repeat(${servicesPerRowMobile}, 1fr)` 
                : `repeat(${servicesPerRow}, 1fr)`,
            // ✅ GAP: 0 für bordered-minimal, bordered-minimal-clean und minimal-four, sonst Standard-Gap
            gap: (data.layoutType === 'bordered-minimal' || data.serviceLayout === 'bordered-minimal-clean' || data.serviceLayout === 'minimal-four') 
                ? '0px' 
                : appendPx(data.serviceSpacing !== undefined ? data.serviceSpacing : 24),
            alignItems: 'stretch', // Gleiche Höhe für alle Cards
            width: '100%',
            maxWidth: appendPx(data.maxWidth || 1200),
            margin: '0 auto'
            // ✅ PADDING ENTFERNT: Kein seitliches Padding mehr im Grid
        };
    };

    // Get service card style based on layout
    const getServiceCardStyle = (index) => {
        const servicesPerRow = data.servicesPerRow || 2;
        const isFirstInRow = index % servicesPerRow === 0;
        const isLastInRow = (index + 1) % servicesPerRow === 0;
        const isLastService = index === services.length - 1;
        
        const baseStyle = {
            backgroundColor: data.serviceBackgroundColor || '#ffffff',
            borderRadius: appendPx(data.serviceBorderRadius !== undefined ? data.serviceBorderRadius : 16),
            // ✅ SEPARATE PADDING CONTROL: Vertikal und Horizontal getrennt
            paddingTop: appendPx(data.servicePadding || 32),
            paddingBottom: appendPx(data.servicePadding || 32),
            paddingLeft: appendPx(data.servicePaddingHorizontal || 32),
            paddingRight: appendPx(data.servicePaddingHorizontal || 32),
            boxShadow: data.serviceShadow !== false ? '0 4px 12px rgba(0, 0, 0, 0.08)' : 'none',
            
            // ✅ SMART BORDERS: Desktop/Mobile optimierte Border-Logik (bordered-minimal, bordered-minimal-clean, minimal-four)
            ...((data.layoutType === 'bordered-minimal' || data.serviceLayout === 'bordered-minimal-clean' || data.serviceLayout === 'minimal-four') ? {
                // ✅ TOP BORDER: Spezielle Logic für minimal-four
                borderTop: (() => {
                    if (data.serviceLayout === 'minimal-four' && !isMobileLayout) {
                        // Desktop 2 Spalten: Letzten 2 Container (untere Reihe) haben keinen oberen Border
                        const totalServices = services.length;
                        const isBottomRow = index >= totalServices - 2; // Letzten 2 Services
                        return isBottomRow ? 
                            'none' : 
                            `${data.serviceBorderWidth || 1}px solid ${data.serviceBorderColor || '#e5e5e5'}`;
                    } else {
                        // Alle anderen Layouts: Alle Container haben oben Border
                        return `${data.serviceBorderWidth || 1}px solid ${data.serviceBorderColor || '#e5e5e5'}`;
                    }
                })(),
                
                // ✅ BOTTOM BORDER: Mobile nur letzter, Desktop alle
                borderBottom: isMobileLayout ? 
                    (isLastService ? `${data.serviceBorderWidth || 1}px solid ${data.serviceBorderColor || '#e5e5e5'}` : 'none') :
                    `${data.serviceBorderWidth || 1}px solid ${data.serviceBorderColor || '#e5e5e5'}`,
                
                // ✅ LEFT BORDER: Mobile nie, Desktop nur erster
                borderLeft: isMobileLayout ? 
                    'none' : 
                    (isFirstInRow ? 'none' : 'none'), // Desktop: Kein linker Border
                
                // ✅ RIGHT BORDER: Mobile nie, Desktop als Trennlinie (außer letzter)
                // ✅ SPECIAL: Für minimal-four (2 Spalten) nur zwischen den 2 Spalten einen Border
                borderRight: isMobileLayout ? 
                    'none' : 
                    (() => {
                        if (data.serviceLayout === 'minimal-four') {
                            // 2 Spalten: Nur linke Spalte hat rechten Border
                            return (index % 2 === 0 && !isLastService) ? 
                                `${data.serviceBorderWidth || 1}px solid ${data.serviceBorderColor || '#e5e5e5'}` : 
                                'none';
                        } else {
                            // 4 Spalten: Original Logic
                            return (isLastInRow || isLastService) ? 
                                'none' : 
                                `${data.serviceBorderWidth || 1}px solid ${data.serviceBorderColor || '#e5e5e5'}`;
                        }
                    })()
            } : (data.serviceBorder ? {
                border: `${data.serviceBorderWidth || 1}px solid ${data.serviceBorderColor || '#e0e0e0'}`
            } : {
                border: 'none'
            })),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center', // Vertikal zentrieren des Inhalts
            textAlign: 'center',
            gap: '16px',
            minHeight: appendPx(data.serviceMinHeight || 280), // Mindesthöhe für konsistente Card-Größen
            height: 'fit-content',
            transition: data.serviceHoverEffect !== false ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
            cursor: 'default'
        };

        // Layout-specific styling - respektiert Edit-Einstellungen
        let layoutStyle = { ...baseStyle };
        
        // Helper function to adjust color brightness
        const adjustColorBrightness = (color, percent) => {
            if (!color || !color.startsWith('#')) return color;
            const num = parseInt(color.replace("#", ""), 16);
            const amt = Math.round(2.55 * percent);
            const R = (num >> 16) + amt;
            const G = (num >> 8 & 0x00FF) + amt;
            const B = (num & 0x0000FF) + amt;
            return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
                (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
                (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
        };
        
        const primaryColor = data.serviceIconColor || '#667eea';
        const backgroundColor = data.serviceBackgroundColor || '#ffffff';
        
        switch (data.serviceLayout) {
            case 'neon-glow':
                layoutStyle = {
                    ...layoutStyle,
                    background: 'linear-gradient(145deg, #1a1a2e, #16213e)',
                    border: `1px solid ${primaryColor}33`, // 33 = 20% opacity
                    boxShadow: `0 0 20px ${primaryColor}1A`, // 1A = 10% opacity
                    color: '#ffffff'
                };
                break;
            
            case 'gradient-cards':
                layoutStyle = {
                    ...layoutStyle,
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColorBrightness(primaryColor, -20)} 100%)`,
                    backgroundSize: '200% 200%',
                    color: '#ffffff',
                    border: 'none'
                };
                break;
            
            case 'cyber-tech':
                layoutStyle = {
                    ...layoutStyle,
                    background: 'linear-gradient(145deg, #0f0f23, #1a1a2e)',
                    border: `2px solid ${primaryColor}`,
                    boxShadow: `0 0 10px ${primaryColor}4D, inset 0 0 10px ${primaryColor}1A`, // 4D = 30%, 1A = 10%
                    color: primaryColor
                };
                break;
            
            case 'floating-cards':
                layoutStyle = {
                    ...layoutStyle,
                    background: backgroundColor,
                    border: 'none',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                };
                break;
            
            case 'minimal-cards':
                layoutStyle = {
                    ...layoutStyle,
                    background: backgroundColor,
                    border: `${data.serviceBorderWidth || 1}px solid ${data.serviceBorderColor || '#e5e5e5'}`,
                    borderRadius: '0px', // ✅ KANTIGE BORDERS
                    boxShadow: 'none' // ✅ KEIN SCHATTEN
                };
                break;
                
            case 'minimal-clean':
                layoutStyle = {
                    ...layoutStyle,
                    background: backgroundColor,
                    border: '1px solid #f0f0f0',
                    boxShadow: 'none'
                };
                break;
                
            case 'luxury-cards':
                layoutStyle = {
                    ...layoutStyle,
                    background: layoutStyle.backgroundColor === '#ffffff' ? 
                        'linear-gradient(135deg, #fff9e6 0%, #ffffff 50%, #fef7f0 100%)' : 
                        layoutStyle.backgroundColor,
                    position: 'relative',
                    overflow: 'hidden'
                };
                // Nur Border überschreiben wenn nicht vom User gesetzt
                if (!data.serviceBorder) {
                    layoutStyle.border = '2px solid rgba(212, 165, 116, 0.2)';
                }
                break;
                
            case 'glass-panels':
                layoutStyle = {
                    ...layoutStyle,
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                };
                break;
                
                
            case 'icon-left':
                layoutStyle = {
                    ...layoutStyle,
                    flexDirection: 'row',
                    textAlign: 'left',
                    alignItems: 'flex-start',
                    gap: '20px'
                };
                break;
                
            case 'masonry-flow':
                // Masonry respektiert User-Background, hat aber Fallback-Gradient
                if (layoutStyle.backgroundColor === '#ffffff') {
                    layoutStyle.background = 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)';
                }
                break;
                
            case 'three-column-clean':
                // ✅ THREE COLUMN CLEAN: Clean Design wie clean-image-cards
                layoutStyle = {
                    ...layoutStyle,
                    background: backgroundColor,
                    border: 'none',
                    borderRadius: '0px', // ✅ KANTIGE SERVICE CARDS
                    boxShadow: 'none' // ✅ KEIN SCHATTEN
                };
                break;
                
            default: // classic-grid
                // Classic Grid respektiert User-Background, hat aber Fallback-Gradient
                if (layoutStyle.backgroundColor === '#ffffff') {
                    layoutStyle.background = 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)';
                }
                break;
        }
        
        return layoutStyle;
    };

    // Get text color based on layout
    const getTextColor = (defaultColor, isTitle = false) => {
        const darkLayouts = ['neon-glow', 'gradient-cards', 'cyber-tech'];
        
        if (darkLayouts.includes(data.serviceLayout)) {
            return '#ffffff';
        }
        
        if (data.serviceLayout === 'glass-panels') {
            return '#333333';
        }
        
        return defaultColor;
    };


    // ✅ NEW: Vereinfachtes Service Media Rendering (Icon oder Image)
    const renderServiceMedia = (service, index) => {
        if (!service) {
            console.warn('⚠️ SimplisticServices: Invalid service at index', index);
            return null;
        }
        
        // ✅ EINFACHE LOGIC: useImage Toggle bestimmt was angezeigt wird
        const useImage = service.useImage === true;
        const hasImage = !!service.serviceImage;
        const hasIcon = !!(service.icon || service.customIcon);
        
        // ✅ PRIORITÄT: Image-Modus zeigt IMMER Image-Container (auch ohne Bild), Icon-Modus zeigt Icon
        const shouldShowImage = useImage; // Zeige Image-Container wenn Toggle aktiviert
        const shouldShowIcon = !useImage && hasIcon; // Zeige Icon nur wenn Toggle deaktiviert UND Icon vorhanden
        const shouldShowFallback = !useImage && !hasIcon; // Fallback nur bei Icon-Modus ohne Icon
        
        console.log('🎨 Simplified service media decision:', {
            serviceIndex: index,
            useImage: useImage,
            hasImage: hasImage,
            hasIcon: hasIcon,
            shouldShowImage: shouldShowImage,
            shouldShowIcon: shouldShowIcon,
            shouldShowFallback: shouldShowFallback,
            finalChoice: shouldShowImage ? 'IMAGE' : (shouldShowIcon ? 'ICON' : 'FALLBACK')
        });
        
        if (shouldShowImage) {
            // ✅ IMAGE RENDERING - Zeige Image oder schönen Placeholder
            console.log('🖼️ Rendering service image container:', { 
                serviceIndex: index, 
                imageSrc: service.serviceImage, 
                useImage: service.useImage,
                hasImage: hasImage
            });
            
            const containerStyle = {
                // ✅ RESPONSIVE BREITE/HÖHE für Images
                width: appendPx(isMobileLayout ? 
                    (data.serviceImageWidthMobile || data.serviceImageWidth || data.serviceIconSize || 60) :
                    (data.serviceImageWidth || data.serviceIconSize || 60)
                ),
                height: appendPx(isMobileLayout ? 
                    (data.serviceImageHeightMobile || data.serviceImageHeight || data.serviceIconSize || 60) :
                    (data.serviceImageHeight || data.serviceIconSize || 60)
                ),
                borderRadius: appendPx(data.serviceIconBorderRadius !== undefined ? data.serviceIconBorderRadius : 12),
                overflow: 'hidden',
                flexShrink: 0,
                border: data.serviceIconBorderWidth > 0 ? 
                    `${data.serviceIconBorderWidth}px solid #000000` : 
                    'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // ✅ IMAGE SHADOW - Optional shadow for images
                boxShadow: data.serviceImageShadow ? 
                    '0 4px 12px rgba(0, 0, 0, 0.15)' : 
                    'none',
                // ✅ IMAGE TOP MARGIN - Responsive top margin
                marginTop: appendPx(isMobileLayout ? 
                    (data.serviceImageTopMarginMobile !== undefined ? data.serviceImageTopMarginMobile : data.serviceImageTopMargin || 0) : 
                    (data.serviceImageTopMargin || 0)
                )
            };
            
            if (hasImage) {
                // ✅ ECHTES IMAGE ANZEIGEN
                return (
                    <div style={{ ...containerStyle, backgroundColor: '#f8f9fa' }}>
                        <img
                            src={service.serviceImage}
                            alt={service.name || 'Service Image'}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                            onError={(e) => {
                                console.warn('⚠️ Service image failed to load:', service.serviceImage);
                                // Fallback zu Placeholder
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = `
                                    <div style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); color: #94a3b8; font-size: 10px;">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                            <circle cx="8.5" cy="8.5" r="1.5"/>
                                            <polyline points="21,15 16,10 5,21"/>
                                        </svg>
                                        <span style="margin-top: 2px;">Error</span>
                                    </div>
                                `;
                            }}
                            onLoad={() => {
                                console.log('✅ Service image loaded successfully:', service.serviceImage);
                            }}
                        />
                    </div>
                );
            } else {
                // ✅ SCHÖNER IMAGE-PLACEHOLDER wenn kein Image hochgeladen
                return (
                    <div style={{
                        ...containerStyle,
                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                        color: '#94a3b8',
                        flexDirection: 'column',
                        gap: '4px'
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21,15 16,10 5,21"/>
                        </svg>
                        <span style={{ fontSize: '9px', fontWeight: '500' }}>Image</span>
                    </div>
                );
            }
        }
        
        // ✅ ICON RENDERING (Default oder wenn Image nicht verfügbar)
        const iconStyle = {
            width: appendPx(data.serviceIconSize || 60),
            height: appendPx(data.serviceIconSize || 60),
            backgroundColor: data.serviceIconBackgroundColor || 'rgba(102, 126, 234, 0.1)',
            borderRadius: appendPx(data.serviceIconBorderRadius || 12),
            padding: appendPx(data.serviceIconPadding || 16),
            // ✅ ICON BORDER: Immer 1px schwarzer Border wenn serviceIconBorderWidth > 0
            border: data.serviceIconBorderWidth > 0 ? 
                `${data.serviceIconBorderWidth}px solid #000000` : 
                'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            // ✅ ICON SHADOW - Konsistent mit Image Shadow
            boxShadow: data.serviceImageShadow ? 
                '0 4px 12px rgba(0, 0, 0, 0.15)' : 
                'none',
            // ✅ ICON TOP MARGIN - Konsistent mit Image Top Margin
            marginTop: appendPx(isMobileLayout ? 
                (data.serviceImageTopMarginMobile !== undefined ? data.serviceImageTopMarginMobile : data.serviceImageTopMargin || 0) : 
                (data.serviceImageTopMargin || 0)
            )
        };

        // ✅ ICON COLOR FILTER: Konvertiere Hex-Farbe zu CSS-Filter für zuverlässige Einfärbung
        const getIconFilter = () => {
            // Invert Toggle hat Priorität
            if (data.invertIconColors === true) {
                return 'invert(1)';
            }
            
            // Icon-Farbe anwenden (funktioniert mit allen SVG-Icons)
            const iconColor = data.serviceIconColor || '#667eea';
            
            // Häufige Farben mit optimierten Filtern
            const colorMap = {
                '#000000': 'brightness(0)',
                '#ffffff': 'brightness(0) invert(1)',
                '#667eea': 'none', // Standard-Farbe
                '#ff0000': 'brightness(0) saturate(100%) invert(13%) sepia(93%) saturate(7471%) hue-rotate(356deg)',
                '#00ff00': 'brightness(0) saturate(100%) invert(50%) sepia(96%) saturate(2341%) hue-rotate(90deg)',
                '#0000ff': 'brightness(0) saturate(100%) invert(20%) sepia(100%) saturate(7500%) hue-rotate(240deg)',
                '#10b981': 'brightness(0) saturate(100%) invert(69%) sepia(25%) saturate(1352%) hue-rotate(140deg)',
                '#3b82f6': 'brightness(0) saturate(100%) invert(45%) sepia(100%) saturate(1352%) hue-rotate(220deg)',
                '#ef4444': 'brightness(0) saturate(100%) invert(25%) sepia(100%) saturate(1352%) hue-rotate(350deg)',
                '#8b5cf6': 'brightness(0) saturate(100%) invert(55%) sepia(50%) saturate(1352%) hue-rotate(260deg)',
                '#f59e0b': 'brightness(0) saturate(100%) invert(60%) sepia(100%) saturate(1352%) hue-rotate(35deg)',
                '#06b6d4': 'brightness(0) saturate(100%) invert(60%) sepia(100%) saturate(1352%) hue-rotate(180deg)'
            };
            
            // ✅ FIX: Fallback ohne invert(1) - verwende 'none' für unbekannte Farben
            const filter = colorMap[iconColor.toLowerCase()] || 'none';
            
            return filter;
        };

        const imageStyle = {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            // ✅ ZUVERLÄSSIGE ICON-EINFÄRBUNG: CSS-Filter funktioniert mit allen SVG-Icons
            filter: getIconFilter()
        };

        // Icon source bestimmen
        const iconSrc = service.iconType === 'upload' && service.customIcon 
            ? service.customIcon 
            : service.icon;

        console.log('🔍 Icon source decision:', {
            serviceIndex: index,
            iconType: service.iconType,
            customIcon: service.customIcon,
            icon: service.icon,
            finalIconSrc: iconSrc,
            shouldShowIcon: shouldShowIcon
        });

        if (shouldShowIcon && iconSrc) {
            // ✅ ICON RENDERING
            console.log('⚡ Rendering service icon:', { 
                serviceIndex: index, 
                iconSrc: iconSrc, 
                iconType: service.iconType,
                customIcon: service.customIcon,
                icon: service.icon
            });
            
            return (
                <div style={{
                    ...iconStyle,
                    // Container-Farbe für SVG-Icons über color-Property verfügbar machen
                    color: data.serviceIconColor || '#667eea'
                }}>
                    <img
                        src={iconSrc}
                        alt={service.name}
                        style={imageStyle}
                        onError={(e) => {
                            // Fallback wenn Icon nicht lädt
                            e.target.style.display = 'none';
                            e.target.parentNode.innerHTML = `<div style="width: 24px; height: 24px; background-color: ${data.serviceIconColor || '#667eea'}; border-radius: 4px;"></div>`;
                        }}
                    />
                </div>
            );
        }
        
        // ✅ FALLBACK: Icon-Modus ohne Icon zeigt Icon-Placeholder
        console.log('🔍 Icon mode but no icon available, showing icon placeholder for service:', index);
        return (
            <div style={iconStyle}>
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    color: '#94a3b8',
                    borderRadius: appendPx(data.serviceIconBorderRadius !== undefined ? data.serviceIconBorderRadius : 12),
                    gap: '4px'
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"/>
                    </svg>
                    <span style={{ fontSize: '9px', fontWeight: '500' }}>Icon</span>
                </div>
            </div>
        );
    };

    // ✅ LEGACY: Icon rendering helper (für Backwards Compatibility)
    const renderServiceIcon = (service, index) => {
        return renderServiceMedia(service, index);
    };

    return (
        <>
            {/* CSS Animations */}
            <style>
                {`
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                    
                    @keyframes simplicisticServicesUploadSpin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    @keyframes silverGlow {
                        0% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                        50% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.04) 0%,
                                rgba(192, 192, 192, 0.05) 25%,
                                rgba(255, 255, 255, 0.06) 50%,
                                rgba(192, 192, 192, 0.05) 75%,
                                rgba(255, 255, 255, 0.04) 100%
                            );
                        }
                        100% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                    }
                `}
            </style>
            <div 
                className={`simplistic-services-widget${isMobileLayout ? ' mobile-layout' : ''}`}
                style={widgetStyle}
                ref={containerRef}
                onClick={handleWidgetClick}
            >
            {/* Delete Button */}
            <div>
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                
                {/* 🤖 AI REFRESH ICON */}
                {isEditMode && (
                    <AIRefreshIcon 
                        widget={{
                            ...data,
                            type: 'SimplisticServices'
                        }}
                        spaceId={null}
                        onContentRefreshed={(refreshedWidget) => {
                            console.log('🤖 [SIMPLICISTICSERVICES] AI-refreshed content received:', refreshedWidget);
                            onUpdate(refreshedWidget);
                        }}
                        position="top-left"
                    />
                )}
                
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={(event) => handleDelete(event, data.id)}
                    />
                )}
            </div>

            {/* Content Container - Immer vertikal und zentriert */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: (data.layoutType === 'bordered-minimal' || data.serviceLayout === 'bordered-minimal-clean') ? '0px' : appendPx(data.contentSpacing || 40),
                alignItems: 'center',
                textAlign: 'center',
                maxWidth: appendPx(data.maxWidth || 1200),
                margin: '0 auto',
                // ✅ BORDERED MINIMAL: Nur Top/Bottom Border für den Container (NICHT für clean version)
                ...(data.layoutType === 'bordered-minimal' && data.serviceLayout !== 'bordered-minimal-clean' && {
                    borderTop: `${data.serviceBorderWidth || 1}px solid ${data.containerBorderColor || '#e5e5e5'}`,
                    borderBottom: `${data.serviceBorderWidth || 1}px solid ${data.containerBorderColor || '#e5e5e5'}`,
                    borderRadius: '0px'
                })
            }}>

                {/* Header Section - Bordered Minimal Style (versteckt für clean version) */}
                {data.serviceLayout !== 'bordered-minimal-clean' && (
                <div style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    textAlign: 'center',
                    // ✅ BORDERED MINIMAL: Header OHNE Bottom Border
                    ...(data.layoutType === 'bordered-minimal' && {
                        padding: '30px 40px'
                        // ✅ KEIN BACKGROUND: Transparenter Header
                        // ✅ KEIN BOTTOM BORDER: Direkte Verbindung zu Services
                    })
                }}>
                    {/* Title - Inline Editable mit HighlightText Support */}
                    {data.showTitleText !== false && (
                        <div>
                            {/* ✅ CLEAN CONDITION: Inline-Editing nur wenn KEINE Highlights aktiv */}
                            {(!data.titleHighlightWords || data.titleHighlightWords.trim() === '') ? (
                                // 🔧 NORMALE INLINE-EDITING (ohne Highlights)
                                renderEditableText(
                                    'titleText',
                                    data.titleText,
                                    'h2',
                                    {
                                        // Title Kombinierte Effects
                                        ...((!data.titleEffect || data.titleEffect === 'none') && {
                                            color: data.titleColor || '#000000'
                                        }),
                                        ...getTextEffectStyles(data.titleEffect),
                                        
                                        fontSize: appendPx(isMobileLayout ? 
                                            (data.titleSizeMobile || data.titleSize || 32) : 
                                            (data.titleSize || 32)
                                        ),
                                        fontWeight: isMobileLayout ? 
                                            (data.titleWeightMobile || data.titleWeight || '600') : 
                                            (data.titleWeight || '600'),
                                        fontFamily: data.titleFont || fonts.globalBigFont,
                                        margin: '0',
                                        // ✅ TITLE BOTTOM MARGIN: Einstellbarer Abstand zum Subtitle
                                        marginBottom: appendPx(data.titleBottomMargin || 16),
                                        lineHeight: '1.3',
                                        // ✅ BORDERED MINIMAL: Elegante Titel-Typografie
                                        ...(data.layoutType === 'bordered-minimal' && {
                                            fontSize: isMobileLayout ? '14px' : '16px',
                                            fontWeight: '500',
                                            textTransform: 'uppercase',
                                            letterSpacing: '2px',
                                            color: '#666666'
                                        }),
                                        // Responsive Font-Sizes
                                        '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 32),
                                        '--normal-size': appendPx(data.titleSize || 32),
                                        '--big-size': appendPx(data.titleSizeBig || data.titleSize || 32)
                                    },
                                    'Our Services'
                                )
                            ) : (
                                // 🎨 HIGHLIGHT-MODUS (nur Anzeige, kein Inline-Editing)
                                <h2 style={{
                                    // Title Kombinierte Effects
                                    ...((!data.titleEffect || data.titleEffect === 'none') && {
                                        color: data.titleColor || '#000000'
                                    }),
                                    ...getTextEffectStyles(data.titleEffect),
                                    
                                    fontSize: appendPx(isMobileLayout ? 
                                        (data.titleSizeMobile || data.titleSize || 32) : 
                                        (data.titleSize || 32)
                                    ),
                                    fontWeight: isMobileLayout ? 
                                        (data.titleWeightMobile || data.titleWeight || '600') : 
                                        (data.titleWeight || '600'),
                                    fontFamily: data.titleFont || fonts.globalBigFont,
                                    margin: '0',
                                    // ✅ TITLE BOTTOM MARGIN: Einstellbarer Abstand zum Subtitle
                                    marginBottom: appendPx(data.titleBottomMargin || 16),
                                    lineHeight: '1.3',
                                    textAlign: 'center',
                                    whiteSpace: 'pre-wrap',
                                    // ✅ BORDERED MINIMAL: Elegante Titel-Typografie
                                    ...(data.layoutType === 'bordered-minimal' && {
                                        fontSize: isMobileLayout ? '14px' : '16px',
                                        fontWeight: '500',
                                        textTransform: 'uppercase',
                                        letterSpacing: '2px',
                                        color: '#666666'
                                    }),
                                    // Responsive Font-Sizes
                                    '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 32),
                                    '--normal-size': appendPx(data.titleSize || 32),
                                    '--big-size': appendPx(data.titleSizeBig || data.titleSize || 32)
                                }}>
                                    <HighlightText
                                        text={data.titleText}
                                        highlightWords={data.titleHighlightWords}
                                        baseStyle={{}}
                                        highlightFont={data.titleHighlightFont || fonts.globalSmallFont}
                                        highlightColor={data.titleHighlightColor}
                                        highlightWeight={data.titleHighlightWeight}
                                        highlightSize={data.titleHighlightSize || data.titleSize}
                                        highlightSizeMobile={data.titleHighlightSizeMobile || data.titleSizeMobile}
                                        highlightSizeBig={data.titleHighlightSizeBig || data.titleSizeBig}
                                        highlightEffect={data.titleHighlightEffect}
                                        highlightMarginLeft={data.titleHighlightMarginLeft}
                                        highlightMarginRight={data.titleHighlightMarginRight}
                                        getTextEffectStyles={getTextEffectStyles}
                                    />
                                </h2>
                            )}
                        </div>
                    )}

                    {/* Subtitle - Nur wenn nicht bordered-minimal */}
                    {data.showSubtitleText !== false && data.layoutType !== 'bordered-minimal' && renderEditableText(
                        'subtitleText',
                        data.subtitleText,
                        'p',
                        {
                            // Subtitle Kombinierte Effects
                            ...((!data.subtitleEffect || data.subtitleEffect === 'none') && {
                                color: data.subtitleColor || '#666666'
                            }),
                            ...getTextEffectStyles(data.subtitleEffect),
                            
                            fontSize: appendPx(isMobileLayout ? 
                                (data.subtitleSizeMobile || data.subtitleSize || 18) : 
                                (data.subtitleSize || 18)
                            ),
                            fontWeight: isMobileLayout ? 
                                (data.subtitleWeightMobile || data.subtitleWeight || '400') : 
                                (data.subtitleWeight || '400'),
                            fontFamily: data.subtitleFont || fonts.globalSmallFont,
                            margin: '0',
                            lineHeight: '1.6',
                            maxWidth: appendPx(data.subtitleMaxWidth || 800),
                            // Responsive Font-Sizes
                            '--mobile-size': appendPx(data.subtitleSizeMobile || data.subtitleSize || 18),
                            '--normal-size': appendPx(data.subtitleSize || 18),
                            '--big-size': appendPx(data.subtitleSizeBig || data.subtitleSize || 18)
                        },
                        'Discover our comprehensive range of professional services designed to help you achieve your goals.'
                    )}
                </div>
                )}

                {/* Services Grid */}
                {services.length > 0 && (
                    <div style={{
                        ...getLayoutStyle(),
                        width: '100%',
                        // ✅ CONTENT SPACING: Verhindert Rand-Kleben (außer bordered layouts)
                        ...(data.layoutType !== 'bordered-minimal' && 
                           data.serviceLayout !== 'bordered-minimal-clean' && 
                           data.serviceLayout !== 'minimal-four' && {
                            paddingLeft: appendPx(isMobileLayout ? (data.contentSpacingMobile || 20) : (data.contentSpacing || 40)),
                            paddingRight: appendPx(isMobileLayout ? (data.contentSpacingMobile || 20) : (data.contentSpacing || 40))
                        })
                    }}>
                                        {services.map((service, index) => {
                                            const cardStyle = getServiceCardStyle(index);
                                            const isIconLeft = data.serviceLayout === 'icon-left';

                                            // ✅ DEBUG: Service-Details für jeden Service
                                            console.log(`🔍 Rendering service ${index}:`, {
                                                name: service.name,
                                                useImage: service.useImage,
                                                hasServiceImage: !!service.serviceImage,
                                                serviceImageUrl: service.serviceImage,
                                                hasIcon: !!(service.icon || service.customIcon),
                                                iconUrl: service.iconType === 'upload' ? service.customIcon : service.icon,
                                                visible: service.visible,
                                                shouldShowMedia: (data.showServiceIcons !== false) && 
                                                                data.layoutType !== 'bordered-minimal' && 
                                                                data.serviceLayout !== 'minimal-cards' && 
                                                                data.serviceLayout !== 'bordered-minimal-clean' && 
                                                                data.serviceLayout !== 'minimal-four'
                                            });

                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        ...cardStyle,
                                                        '--index': index
                                                    }}
                                                    className={`service-card ${data.serviceLayout || 'classic-grid'}`}
                                                    onMouseEnter={data.serviceHoverEffect !== false ? (e) => {
                                                        e.currentTarget.style.transform = `translateY(-4px) scale(${data.serviceHoverScale || 1.02})`;
                                                        if (data.serviceHoverShadow !== false) {
                                                            e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15)';
                                                        }
                                                    } : undefined}
                                                    onMouseLeave={data.serviceHoverEffect !== false ? (e) => {
                                                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                        e.currentTarget.style.boxShadow = cardStyle.boxShadow;
                                                    } : undefined}
                                                >
                                                    {/* ✅ Service Media - Icon oder Image basierend auf useImage Toggle */}
                                                    {(data.showServiceIcons !== false) && 
                                                     data.layoutType !== 'bordered-minimal' && 
                                                     data.serviceLayout !== 'minimal-cards' && 
                                                     data.serviceLayout !== 'bordered-minimal-clean' && 
                                                     data.serviceLayout !== 'minimal-four' && 
                                                     renderServiceMedia(service, index)}

                                                    {/* Service Content */}
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px',
                                        flex: 1,
                                        minWidth: 0,
                                        textAlign: 'center', // ✅ IMMER ZENTRIERT
                                        height: '100%', // ✅ VOLLE HÖHE für perfekte Zentrierung
                                        // ✅ SERVICE TEXT MAX WIDTH - Slider-basiert (0 = unlimited)
                                        maxWidth: (() => {
                                            const maxWidthValue = isMobileLayout ? 
                                                (data.serviceTextMaxWidthMobile || data.serviceTextMaxWidth || 0) : 
                                                (data.serviceTextMaxWidth || 0);
                                            
                                            // Wenn Wert 0 ist = unlimited (none)
                                            return maxWidthValue > 0 ? appendPx(maxWidthValue) : 'none';
                                        })(),
                                        // ✅ SERVICE TEXT MIN HEIGHT - Slider-basiert (0 = auto)
                                        minHeight: (() => {
                                            const minHeightValue = isMobileLayout ? 
                                                (data.serviceTextMinHeightMobile || data.serviceTextMinHeight || 0) : 
                                                (data.serviceTextMinHeight || 0);
                                            
                                            // Wenn Wert 0 ist = auto height
                                            return minHeightValue > 0 ? appendPx(minHeightValue) : 'auto';
                                        })(),
                                        width: '100%' // ✅ Volle Breite bis zur maxWidth
                                    }}>
                                        {/* Service Name */}
                                        <h3 style={{
                                            fontSize: appendPx(data.serviceNameSize || 20),
                                            fontWeight: data.serviceNameWeight || '600',
                                            fontFamily: data.serviceNameFont || fonts.globalBigFont,
                                            color: getTextColor(data.serviceNameColor || '#000000', true),
                                            margin: '0',
                                            lineHeight: '1.4'
                                        }}>
                                            {service.name}
                                        </h3>

                                        {/* ✅ SERVICE PRICE - Spezielle Reihenfolge für three-column-clean */}
                                        {data.showServicePrices !== false && service.price && data.serviceLayout === 'three-column-clean' && (
                                            <div style={{
                                                fontSize: isMobileLayout ? appendPx(data.servicePriceSizeMobile || 16) : appendPx(data.servicePriceSize || 18),
                                                fontWeight: data.servicePriceWeight || '600',
                                                fontFamily: data.serviceDescriptionFont || fonts.globalSmallFont,
                                                color: data.serviceNameColor || '#000000', // ✅ GLEICHE FARBE WIE TITEL
                                                margin: '4px 0 0 0', // ✅ WENIGER MARGIN für engere Bindung zum Titel
                                                lineHeight: '1.4',
                                                textAlign: 'center'
                                            }}>
                                                {service.price}
                                            </div>
                                        )}

                                        {/* Service Description */}
                                        <p style={{
                                            fontSize: appendPx(data.serviceDescriptionSize || 16),
                                            fontWeight: data.serviceDescriptionWeight || '400',
                                            fontFamily: data.serviceDescriptionFont || fonts.globalSmallFont,
                                            color: getTextColor(data.serviceDescriptionColor || '#666666', false),
                                            margin: '0',
                                            lineHeight: '1.6',
                                            // ✅ DESCRIPTION MIN HEIGHT - Für einheitliche Button-Platzierung
                                            minHeight: (() => {
                                                const minHeightValue = isMobileLayout ? 
                                                    (data.serviceDescriptionMinHeightMobile || data.serviceDescriptionMinHeight || 0) : 
                                                    (data.serviceDescriptionMinHeight || 0);
                                                
                                                // Wenn Wert 0 ist = auto height
                                                return minHeightValue > 0 ? appendPx(minHeightValue) : 'auto';
                                            })(),
                                            // ✅ DISPLAY FLEX für bessere Höhen-Kontrolle
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {service.description}
                                        </p>

                                        {/* Service Price - Standard Position für alle anderen Layouts */}
                                        {data.showServicePrices !== false && service.price && data.serviceLayout !== 'three-column-clean' && (
                                            <div style={{
                                                fontSize: isMobileLayout ? appendPx(data.servicePriceSizeMobile || 16) : appendPx(data.servicePriceSize || 18),
                                                fontWeight: data.servicePriceWeight || '600',
                                                fontFamily: data.serviceDescriptionFont || fonts.globalSmallFont,
                                                color: data.serviceNameColor || '#000000', // ✅ GLEICHE FARBE WIE TITEL
                                                margin: '8px 0 0 0',
                                                lineHeight: '1.4',
                                                textAlign: 'center'
                                            }}>
                                                {service.price}
                                            </div>
                                        )}

                                        {/* ✅ SERVICE BUTTON - NEU */}
                                        {data.showServiceButtons !== false && service.showButton !== false && service.buttonText && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (service.buttonLink && service.buttonLink !== '#') {
                                                        if (service.buttonLink.startsWith('http')) {
                                                            window.open(service.buttonLink, '_blank');
                                                        } else {
                                                            window.location.href = service.buttonLink;
                                                        }
                                                    }
                                                }}
                                                style={{
                                                    // ✅ BUTTON STYLING
                                                    backgroundColor: data.serviceButtonColor || '#667eea',
                                                    color: data.serviceButtonTextColor || '#ffffff',
                                                    fontSize: isMobileLayout ? appendPx(data.serviceButtonSizeMobile || 13) : appendPx(data.serviceButtonSize || 14),
                                                    fontWeight: data.serviceButtonWeight || '500',
                                                    fontFamily: data.serviceButtonFont || data.serviceDescriptionFont || fonts.globalSmallFont,
                                                    
                                                    // ✅ BUTTON SIZE - Slider-basierte Width & Height
                                                    width: appendPx(isMobileLayout ? 
                                                        (data.serviceButtonWidthPxMobile || data.serviceButtonWidthPx || 120) : 
                                                        (data.serviceButtonWidthPx || 120)
                                                    ),
                                                    height: appendPx(isMobileLayout ? 
                                                        (data.serviceButtonHeightPxMobile || data.serviceButtonHeightPx || 44) : 
                                                        (data.serviceButtonHeightPx || 44)
                                                    ),
                                                    
                                                    // ✅ BUTTON PADDING - Unterstützt 0px
                                                    paddingTop: appendPx(data.serviceButtonPadding !== undefined ? data.serviceButtonPadding : 12),
                                                    paddingBottom: appendPx(data.serviceButtonPadding !== undefined ? data.serviceButtonPadding : 12),
                                                    paddingLeft: appendPx(data.serviceButtonPaddingHorizontal !== undefined ? data.serviceButtonPaddingHorizontal : 24),
                                                    paddingRight: appendPx(data.serviceButtonPaddingHorizontal !== undefined ? data.serviceButtonPaddingHorizontal : 24),
                                                    
                                                    // ✅ BUTTON BORDERS & RADIUS - Unterstützt 0px
                                                    border: (data.serviceButtonBorder && (data.serviceButtonBorderWidth !== undefined ? data.serviceButtonBorderWidth : 1) > 0) ? 
                                                        `${data.serviceButtonBorderWidth !== undefined ? data.serviceButtonBorderWidth : 1}px solid ${data.serviceButtonBorderColor || data.serviceButtonColor || '#667eea'}` : 
                                                        'none',
                                                    borderRadius: appendPx(data.serviceButtonBorderRadius !== undefined ? data.serviceButtonBorderRadius : 8),
                                                    
                                                    // ✅ BUTTON SHADOW
                                                    boxShadow: data.serviceButtonShadow !== false ? 
                                                        '0 2px 4px rgba(0, 0, 0, 0.1)' : 
                                                        'none',
                                                    
                                                    // ✅ BUTTON BEHAVIOR
                                                    cursor: 'pointer',
                                                    transition: data.serviceButtonHoverEffect !== false ? 
                                                        'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 
                                                        'none',
                                                    outline: 'none',
                                                    textDecoration: 'none',
                                                    display: 'inline-flex', // ✅ FLEX für bessere Zentrierung bei festen Größen
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    textAlign: 'center',
                                                    lineHeight: '1.2',
                                                    marginTop: '16px',
                                                    
                                                    // ✅ BOX SIZING für korrekte Größenberechnung
                                                    boxSizing: 'border-box'
                                                }}
                                                onMouseEnter={data.serviceButtonHoverEffect !== false ? (e) => {
                                                    e.target.style.backgroundColor = data.serviceButtonHoverColor || '#5a67d8';
                                                    e.target.style.transform = 'translateY(-1px)';
                                                    if (data.serviceButtonShadow !== false) {
                                                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                                    }
                                                } : undefined}
                                                onMouseLeave={data.serviceButtonHoverEffect !== false ? (e) => {
                                                    e.target.style.backgroundColor = data.serviceButtonColor || '#667eea';
                                                    e.target.style.transform = 'translateY(0)';
                                                    e.target.style.boxShadow = data.serviceButtonShadow !== false ? 
                                                        '0 2px 4px rgba(0, 0, 0, 0.1)' : 
                                                        'none';
                                                } : undefined}
                                                onFocus={(e) => {
                                                    e.target.style.boxShadow = `0 0 0 3px ${data.serviceButtonColor || '#667eea'}33`;
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.boxShadow = data.serviceButtonShadow !== false ? 
                                                        '0 2px 4px rgba(0, 0, 0, 0.1)' : 
                                                        'none';
                                                }}
                                            >
                                                {service.buttonText}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Fallback wenn keine Services */}
                {services.length === 0 && (
                    <div style={{
                        padding: '40px',
                        border: isEditMode ? '2px dashed #dee2e6' : '1px solid #e9ecef',
                        borderRadius: '12px',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        textAlign: 'center'
                    }}>
                        <p style={{ margin: 0, fontSize: '16px' }}>
                            {isEditMode ? 'Füge Services über das Edit-Panel hinzu' : 'No services available'}
                        </p>
                    </div>
                )}

                {/* Subtitle NACH den Services - nur für bordered-minimal */}
                {data.showSubtitleText !== false && data.layoutType === 'bordered-minimal' && (
                    <div style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}>
                        {renderEditableText(
                            'subtitleText',
                            data.subtitleText,
                            'p',
                            {
                                // Subtitle Kombinierte Effects
                                ...((!data.subtitleEffect || data.subtitleEffect === 'none') && {
                                    color: data.subtitleColor || '#666666'
                                }),
                                ...getTextEffectStyles(data.subtitleEffect),
                                
                                fontSize: appendPx(isMobileLayout ? 
                                    (data.subtitleSizeMobile || data.subtitleSize || 18) : 
                                    (data.subtitleSize || 18)
                                ),
                                fontWeight: isMobileLayout ? 
                                    (data.subtitleWeightMobile || data.subtitleWeight || '400') : 
                                    (data.subtitleWeight || '400'),
                                fontFamily: data.subtitleFont || fonts.globalSmallFont,
                                margin: '0',
                                lineHeight: '1.6',
                                maxWidth: appendPx(data.subtitleMaxWidth || 800),
                                // ✅ BORDERED MINIMAL: Subtitle-Styling
                                marginTop: '0px',
                                padding: '30px 40px',
                                // ✅ KEIN BACKGROUND: Transparenter Footer
                                fontSize: isMobileLayout ? '14px' : '16px',
                                fontWeight: '400',
                                color: '#666666',
                                textAlign: 'center', // ✅ EXPLIZIT ZENTRIERT
                                // Responsive Font-Sizes
                                '--mobile-size': appendPx(data.subtitleSizeMobile || data.subtitleSize || 18),
                                '--normal-size': appendPx(data.subtitleSize || 18),
                                '--big-size': appendPx(data.subtitleSizeBig || data.subtitleSize || 18)
                            },
                            'Discover our comprehensive range of professional services designed to help you achieve your goals.'
                        )}
                    </div>
                )}
            </div>
            </div>
        </>
    );
}

export default SimplisticServices;
